import { computed, reactive, shallowRef } from 'vue'
import {
  fetchDistributeSearch,
  fetchSearchCartoons,
  fetchSearchCategories,
  fetchSearchPipes,
  fetchSearchPriorityUsers,
  fetchSearchTopics,
  fetchSearchUsers,
} from '@/api/search'
import type {
  SearchCartoonItem,
  SearchCategoryItem,
  SearchPageData,
  SearchPriorityUserItem,
  SearchTopicItem,
  SearchUserItem,
  SearchWorkItem,
} from '@/api/search'
import type { UserWorkItem } from '@/api/home/type'
import { useUserStore } from '@/stores/user'
import { normalizeSearchWorkItems, type SearchHotWordOption } from '@/utils/search/model'

export type SearchResultTab = 'all' | 'cartoon' | 'work' | 'user' | 'topic'

export interface SearchResultTabConfig {
  key: SearchResultTab
  label: string
}

export interface SearchPagedResult<T> {
  items: T[]
  pageNo: number
  hasNext: boolean
  loading: boolean
  loadingMore: boolean
  error: string | null
}

export interface SearchRecommendationSection {
  category: SearchCategoryItem
  items: UserWorkItem[]
}

export type SearchPriorityUserResult = Omit<SearchPriorityUserItem, 'contents'> & {
  contents: UserWorkItem[]
}

export const SEARCH_RESULT_TABS: SearchResultTabConfig[] = [
  { key: 'all', label: '综合' },
  { key: 'cartoon', label: '角色' },
  { key: 'work', label: '作品' },
  { key: 'user', label: '用户' },
  { key: 'topic', label: '话题' },
]

const RECENT_SEARCH_STORAGE_KEY = 'dreama.search.recent.v1'
const RECENT_SEARCH_LIMIT = 18
const SEARCH_RECOMMENDATION_CATEGORY_LIMIT = 3
const SEARCH_RECOMMENDATION_ITEM_LIMIT = 20

class SearchLoginRequiredError extends Error {
  constructor() {
    super('login-required')
  }
}

function createPagedResult<T>(): SearchPagedResult<T> {
  return reactive({
    items: [],
    pageNo: 1,
    hasNext: false,
    loading: false,
    loadingMore: false,
    error: null,
  }) as SearchPagedResult<T>
}

function readRecentSearches() {
  if (typeof localStorage === 'undefined') {
    return []
  }

  try {
    const raw = localStorage.getItem(RECENT_SEARCH_STORAGE_KEY)
    const value = raw ? JSON.parse(raw) : []

    return Array.isArray(value)
      ? value
          .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          .slice(0, RECENT_SEARCH_LIMIT)
      : []
  } catch {
    return []
  }
}

function writeRecentSearches(items: string[]) {
  if (typeof localStorage === 'undefined') {
    return
  }

  localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(items))
}

function assertSuccess<T extends { code: number; msg: string; data: unknown }>(
  response: T,
  fallbackMessage: string,
) {
  if (response.code !== 0) {
    throw new Error(response.msg || fallbackMessage)
  }

  return response.data
}

function isSearchLoginRequiredError(error: unknown) {
  return error instanceof SearchLoginRequiredError
}

function resetPagedResult<T>(state: SearchPagedResult<T>) {
  state.items = []
  state.pageNo = 1
  state.hasNext = false
  state.loading = false
  state.loadingMore = false
  state.error = null
}

function appendUniqueById<T extends { id: string }>(current: T[], incoming: T[]) {
  const existingIds = new Set(current.map((item) => item.id))
  const nextItems = incoming.filter((item) => !existingIds.has(item.id))

  return current.concat(nextItems)
}

function applyPagedData<T extends { id: string }>(
  state: SearchPagedResult<T>,
  data: SearchPageData<T> | null | undefined,
  append: boolean,
) {
  const nextItems = data?.list ?? []

  state.items = append ? appendUniqueById(state.items, nextItems) : nextItems
  state.pageNo = data?.pageNo ?? state.pageNo + 1
  state.hasNext = nextItems.length > 0 && Boolean(data?.hasNext)
}

export function useSearchPage() {
  const userStore = useUserStore()
  const query = shallowRef('')
  const submittedQuery = shallowRef('')
  const activeTab = shallowRef<SearchResultTab>('all')
  const recentSearches = shallowRef<string[]>(readRecentSearches())
  const hotWords = shallowRef<SearchHotWordOption[]>([])
  const recommendations = shallowRef<SearchRecommendationSection[]>([])
  const loadingLanding = shallowRef(false)
  const loadingHotWords = shallowRef(false)
  const landingError = shallowRef<string | null>(null)
  const cartoonResults = createPagedResult<SearchCartoonItem>()
  const workResults = createPagedResult<UserWorkItem>()
  const userResults = createPagedResult<SearchUserItem>()
  const topicResults = createPagedResult<SearchTopicItem>()
  const priorityUsers = shallowRef<SearchPriorityUserResult[]>([])
  const loadingPriorityUsers = shallowRef(false)
  const priorityUsersError = shallowRef<string | null>(null)
  let landingRequestId = 0
  let hotWordsRequestId = 0
  let searchRequestId = 0

  const hasSubmittedQuery = computed(() => submittedQuery.value.length > 0)
  const isSearching = computed(
    () =>
      cartoonResults.loading ||
      workResults.loading ||
      userResults.loading ||
      topicResults.loading ||
      loadingPriorityUsers.value,
  )
  const hasAnyResults = computed(
    () =>
      cartoonResults.items.length > 0 ||
      workResults.items.length > 0 ||
      userResults.items.length > 0 ||
      topicResults.items.length > 0 ||
      priorityUsers.value.length > 0,
  )
  const resultCounts = computed<Record<SearchResultTab, number>>(() => {
    const userCount = userResults.items.length + priorityUsers.value.length
    const total =
      cartoonResults.items.length + workResults.items.length + userCount + topicResults.items.length

    return {
      all: total,
      cartoon: cartoonResults.items.length,
      work: workResults.items.length,
      user: userCount,
      topic: topicResults.items.length,
    }
  })
  const canLoadMoreActiveTab = computed(() => {
    if (activeTab.value === 'all') {
      return workResults.hasNext && !workResults.loadingMore
    }

    if (activeTab.value === 'cartoon') {
      return cartoonResults.hasNext && !cartoonResults.loadingMore
    }

    if (activeTab.value === 'work') {
      return workResults.hasNext && !workResults.loadingMore
    }

    if (activeTab.value === 'user') {
      return userResults.hasNext && !userResults.loadingMore
    }

    if (activeTab.value === 'topic') {
      return topicResults.hasNext && !topicResults.loadingMore
    }

    return false
  })

  function setQuery(nextQuery: string) {
    query.value = nextQuery
  }

  function setActiveTab(tab: SearchResultTab) {
    activeTab.value = tab
  }

  function addRecentSearch(keyword: string) {
    if (!userStore.isLoggedIn()) {
      return
    }

    const normalized = keyword.trim()

    if (!normalized) {
      return
    }

    const nextItems = [
      normalized,
      ...recentSearches.value.filter((item) => item !== normalized),
    ].slice(0, RECENT_SEARCH_LIMIT)

    recentSearches.value = nextItems
    writeRecentSearches(nextItems)
  }

  function removeRecentSearch(keyword: string) {
    const nextItems = recentSearches.value.filter((item) => item !== keyword)

    recentSearches.value = nextItems
    writeRecentSearches(nextItems)
  }

  function clearRecentSearches() {
    recentSearches.value = []
    writeRecentSearches([])
  }

  function resetResults() {
    resetPagedResult(cartoonResults)
    resetPagedResult(workResults)
    resetPagedResult(userResults)
    resetPagedResult(topicResults)
    priorityUsers.value = []
    loadingPriorityUsers.value = false
    priorityUsersError.value = null
  }

  function clearSubmittedSearch() {
    searchRequestId += 1
    query.value = ''
    submittedQuery.value = ''
    activeTab.value = 'all'
    resetResults()
  }

  function assertSearchSuccess<T extends { code: number; msg: string; data: unknown }>(
    response: T,
    fallbackMessage: string,
  ) {
    if (response.code === 302) {
      userStore.resetUserInfo()
      throw new SearchLoginRequiredError()
    }

    return assertSuccess(response, fallbackMessage)
  }

  async function loadHotWords() {
    const requestId = ++hotWordsRequestId

    if (requestId === hotWordsRequestId) {
      hotWords.value = []
      loadingHotWords.value = false
    }
  }

  async function loadLandingData() {
    const requestId = ++landingRequestId
    ++hotWordsRequestId

    loadingLanding.value = true
    loadingHotWords.value = false
    landingError.value = null
    hotWords.value = []

    const recommendationsPromise = fetchSearchCategories().then(async (response) => {
      const data = assertSearchSuccess(response, '搜索推荐类目加载失败') as SearchCategoryItem[]
      const categories = [...data]
        .sort((a, b) => (a.priorityDisplay ?? 0) - (b.priorityDisplay ?? 0))
        .slice(0, SEARCH_RECOMMENDATION_CATEGORY_LIMIT)

      const settledSections = await Promise.allSettled(
        categories.map(async (category) => {
          const sectionResponse = await fetchDistributeSearch({
            categoryId: category.id,
            pageNo: 1,
          })
          const sectionData = assertSearchSuccess(
            sectionResponse,
            '搜索推荐内容加载失败',
          ) as SearchPageData<SearchWorkItem>

          return {
            category,
            items: normalizeSearchWorkItems(sectionData.list).slice(
              0,
              SEARCH_RECOMMENDATION_ITEM_LIMIT,
            ),
          }
        }),
      )

      return settledSections
        .filter(
          (result): result is PromiseFulfilledResult<SearchRecommendationSection> =>
            result.status === 'fulfilled',
        )
        .map((result) => result.value)
        .filter((section) => section.items.length > 0)
    })

    const recommendationResult = await Promise.resolve(recommendationsPromise).then(
      (value) => ({ status: 'fulfilled' as const, value }),
      (reason) => ({ status: 'rejected' as const, reason }),
    )

    if (requestId !== landingRequestId) {
      return
    }

    if (recommendationResult.status === 'fulfilled') {
      recommendations.value = recommendationResult.value
    } else if (isSearchLoginRequiredError(recommendationResult.reason)) {
      recommendations.value = []
    } else {
      recommendations.value = []
      landingError.value =
        recommendationResult.reason instanceof Error
          ? recommendationResult.reason.message
          : '搜索推荐加载失败'
    }

    loadingLanding.value = false
  }

  async function loadCartoons(keyword: string, requestId: number, append = false) {
    cartoonResults[append ? 'loadingMore' : 'loading'] = true
    cartoonResults.error = null

    try {
      const response = await fetchSearchCartoons({
        name: keyword,
        pageNo: append ? cartoonResults.pageNo : 1,
      })
      const data = assertSearchSuccess(
        response,
        '角色搜索失败',
      ) as SearchPageData<SearchCartoonItem>

      if (requestId !== searchRequestId) {
        return
      }

      applyPagedData(cartoonResults, data, append)
    } catch (error) {
      if (requestId === searchRequestId && !isSearchLoginRequiredError(error)) {
        cartoonResults.error = error instanceof Error ? error.message : '角色搜索失败'
      }
    } finally {
      if (requestId === searchRequestId) {
        cartoonResults[append ? 'loadingMore' : 'loading'] = false
      }
    }
  }

  async function loadWorks(keyword: string, requestId: number, append = false) {
    const userId = userStore.getId()

    if (!userId) {
      workResults.error = '缺少用户标识，无法搜索作品'
      workResults.hasNext = false
      return
    }

    workResults[append ? 'loadingMore' : 'loading'] = true
    workResults.error = null

    try {
      const response = await fetchSearchPipes({
        userId,
        name: keyword,
        pageNo: append ? workResults.pageNo : 1,
      })
      const data = assertSearchSuccess(response, '作品搜索失败') as SearchPageData<SearchWorkItem>
      const normalizedData: SearchPageData<UserWorkItem> = {
        ...data,
        list: normalizeSearchWorkItems(data.list),
      }

      if (requestId !== searchRequestId) {
        return
      }

      applyPagedData(workResults, normalizedData, append)
    } catch (error) {
      if (requestId === searchRequestId && !isSearchLoginRequiredError(error)) {
        workResults.error = error instanceof Error ? error.message : '作品搜索失败'
      }
    } finally {
      if (requestId === searchRequestId) {
        workResults[append ? 'loadingMore' : 'loading'] = false
      }
    }
  }

  async function loadUsers(keyword: string, requestId: number, append = false) {
    userResults[append ? 'loadingMore' : 'loading'] = true
    userResults.error = null

    try {
      const response = await fetchSearchUsers({
        name: keyword,
        pageNo: append ? userResults.pageNo : 1,
      })
      const data = assertSearchSuccess(response, '用户搜索失败') as SearchPageData<SearchUserItem>

      if (requestId !== searchRequestId) {
        return
      }

      applyPagedData(userResults, data, append)
    } catch (error) {
      if (requestId === searchRequestId && !isSearchLoginRequiredError(error)) {
        userResults.error = error instanceof Error ? error.message : '用户搜索失败'
      }
    } finally {
      if (requestId === searchRequestId) {
        userResults[append ? 'loadingMore' : 'loading'] = false
      }
    }
  }

  async function loadTopics(keyword: string, requestId: number, append = false) {
    topicResults[append ? 'loadingMore' : 'loading'] = true
    topicResults.error = null

    try {
      const response = await fetchSearchTopics({
        name: keyword,
        pageNo: append ? topicResults.pageNo : 1,
      })
      const data = assertSearchSuccess(response, '话题搜索失败') as SearchPageData<SearchTopicItem>

      if (requestId !== searchRequestId) {
        return
      }

      applyPagedData(topicResults, data, append)
    } catch (error) {
      if (requestId === searchRequestId && !isSearchLoginRequiredError(error)) {
        topicResults.error = error instanceof Error ? error.message : '话题搜索失败'
      }
    } finally {
      if (requestId === searchRequestId) {
        topicResults[append ? 'loadingMore' : 'loading'] = false
      }
    }
  }

  async function loadPriorityUsers(keyword: string, requestId: number) {
    loadingPriorityUsers.value = true
    priorityUsersError.value = null

    try {
      const response = await fetchSearchPriorityUsers({
        name: keyword,
        pageNo: 1,
      })
      const data = assertSearchSuccess(response, '白名单用户搜索失败') as SearchPriorityUserItem[]

      if (requestId !== searchRequestId) {
        return
      }

      priorityUsers.value = data.map((user) => ({
        ...user,
        contents: normalizeSearchWorkItems(user.contents),
      }))
    } catch (error) {
      if (requestId === searchRequestId && !isSearchLoginRequiredError(error)) {
        priorityUsers.value = []
        priorityUsersError.value = error instanceof Error ? error.message : '白名单用户搜索失败'
      }
    } finally {
      if (requestId === searchRequestId) {
        loadingPriorityUsers.value = false
      }
    }
  }

  async function submitSearch(rawKeyword = query.value) {
    const keyword = rawKeyword.trim()

    query.value = keyword

    if (!keyword) {
      clearSubmittedSearch()
      return
    }

    if (!userStore.isLoggedIn()) {
      return
    }

    const requestId = ++searchRequestId

    submittedQuery.value = keyword
    activeTab.value = 'all'
    resetResults()
    addRecentSearch(keyword)

    await Promise.allSettled([
      loadCartoons(keyword, requestId),
      loadWorks(keyword, requestId),
      loadUsers(keyword, requestId),
      loadTopics(keyword, requestId),
      loadPriorityUsers(keyword, requestId),
    ])
  }

  async function loadMoreActiveTab() {
    if (!userStore.isLoggedIn() || !canLoadMoreActiveTab.value || !submittedQuery.value) {
      return
    }

    const requestId = searchRequestId
    const keyword = submittedQuery.value

    if (activeTab.value === 'all' || activeTab.value === 'work') {
      await loadWorks(keyword, requestId, true)
    } else if (activeTab.value === 'cartoon') {
      await loadCartoons(keyword, requestId, true)
    } else if (activeTab.value === 'user') {
      await loadUsers(keyword, requestId, true)
    } else if (activeTab.value === 'topic') {
      await loadTopics(keyword, requestId, true)
    }
  }

  return {
    query,
    submittedQuery,
    activeTab,
    recentSearches,
    hotWords,
    recommendations,
    loadingLanding,
    loadingHotWords,
    landingError,
    cartoonResults,
    workResults,
    userResults,
    topicResults,
    priorityUsers,
    loadingPriorityUsers,
    priorityUsersError,
    hasSubmittedQuery,
    isSearching,
    hasAnyResults,
    resultCounts,
    canLoadMoreActiveTab,
    setQuery,
    setActiveTab,
    submitSearch,
    clearSubmittedSearch,
    loadLandingData,
    loadHotWords,
    loadMoreActiveTab,
    removeRecentSearch,
    clearRecentSearches,
  }
}
